import { THEMEPARKS_WIKI_SOURCE, type WikiShowtime } from "../config/themeparksWiki";
import { useOwningParkExternalId } from "./useOwningPark";
import { useParkWaitTimes } from "./useParkWaitTimes";
import type { EntityType } from "../types";

interface EntityLike {
  type?: EntityType;
  parentId?: string | null;
  externalSource?: string | null;
  externalId?: string | null;
}

export interface LiveWaitTime {
  /** Whether the entity is linked to ThemeParks.wiki at all. */
  linked: boolean;
  waitMinutes: number | null;
  status: string | null;
  showtimes: WikiShowtime[];
  /** The owning park's cache is older than the staleness threshold. */
  stale: boolean;
  /** ms epoch the owning park snapshot was fetched, or null. */
  fetchedAt: number | null;
  loading: boolean;
}

/**
 * Live wait time / status for an ATTRACTION or SHOW that's linked to
 * ThemeParks.wiki. Walks the parentId chain to find the owning park's cache.
 */
export function useLiveWaitTime(entity: EntityLike | null | undefined): LiveWaitTime {
  const linked =
    !!entity &&
    entity.externalSource === THEMEPARKS_WIKI_SOURCE &&
    typeof entity.externalId === "string" &&
    entity.externalId.length > 0;
  const externalId = linked ? (entity!.externalId as string) : null;

  const { parkExternalId, loading: parkLoading } = useOwningParkExternalId(
    linked ? entity : null,
  );
  const { entities, fetchedAt, stale, loading: liveLoading } = useParkWaitTimes(parkExternalId);

  if (!linked || !externalId) {
    return {
      linked: false,
      waitMinutes: null,
      status: null,
      showtimes: [],
      stale: false,
      fetchedAt: null,
      loading: false,
    };
  }

  const snap = entities[externalId];
  return {
    linked: true,
    waitMinutes: typeof snap?.waitMinutes === "number" ? snap.waitMinutes : null,
    status: snap?.status ?? null,
    showtimes: snap?.showtimes ?? [],
    stale,
    fetchedAt,
    loading: parkLoading || liveLoading,
  };
}
